import { useMemo, useRef } from "react";

import { guessContentType } from "../components/Files/files";
import { authHeader, FormsApiError, formsFetch, unwrap } from "../lib/formsApi";
import { type FileActions, showFile } from "../questionnaire/fileActions";
import { PREFILL_MAX_BYTES, prefillAccepts } from "../questionnaire/prefill";

type PickedFile = { fileId: string; key: string; file: File; path?: string };

const SUGGESTABLE_TYPES = ["application/pdf", "image/png", "image/jpeg", "text/plain"] as const;
type SuggestableType = (typeof SUGGESTABLE_TYPES)[number];

const suggestableType = (file: File) => {
    const mimeType = guessContentType(file.name);
    return prefillAccepts(file.name) &&
        file.size <= PREFILL_MAX_BYTES &&
        SUGGESTABLE_TYPES.includes(mimeType as SuggestableType)
        ? (mimeType as SuggestableType)
        : undefined;
};

export const uploadSupportingDocument = async (token: string | undefined, file: File, assessmentId?: string) => {
    const mimeType = suggestableType(file);
    if (!mimeType) return undefined;
    const { url, path, requestHeaders } = unwrap(
        await formsFetch.POST("/api/supporting-documents/upload-url", {
            headers: authHeader(token),
            body: { fileName: file.name, mimeType, assessmentId },
        }),
        "create supporting document upload url"
    );
    const headers = new Headers();
    requestHeaders.forEach((header) => headers.set(header.key, header.value));
    const response = await fetch(url, { method: "PUT", headers, body: file });
    if (!response.ok) throw new Error(`Upload request failed with status ${response.status}`);
    return path;
};

export const deleteSupportingDocument = async (token: string | undefined, path: string, assessmentId?: string) => {
    const { response } = await formsFetch.DELETE("/api/supporting-documents", {
        headers: authHeader(token),
        params: { query: { path, assessmentId } },
    });
    if (!response.ok) throw new FormsApiError("delete supporting document", response.status);
};

export const suggestAnswers = async (token: string | undefined, paths: string[], assessmentId?: string) => {
    if (paths.length === 0) return [];
    const { suggestions } = unwrap(
        await formsFetch.POST("/api/suggest-answers", { headers: authHeader(token), body: { paths, assessmentId } }),
        "suggest answers"
    );
    return suggestions;
};

export const usePreviewFileActions = (token: string | undefined): FileActions => {
    const latest = useRef(token);
    latest.current = token;
    const picked = useRef<PickedFile[]>([]);

    return useMemo(() => {
        const find = (key: string, fileName: string) =>
            picked.current.find((p) => p.key === key && p.file.name === fileName);
        return {
            upload: async (key, _answer, file) => {
                const fileId = crypto.randomUUID();
                const path = await uploadSupportingDocument(latest.current, file).catch(() => undefined);
                picked.current = [
                    ...picked.current.filter((p) => !(p.key === key && p.file.name === file.name)),
                    { fileId, key, file, path },
                ];
                return { fileName: file.name, fileId };
            },
            download: async (key, fileName) => {
                const file = find(key, fileName)?.file;
                if (!file) throw new Error("file is not in this preview");
                showFile(file, fileName);
            },
            remove: async (key, fileName) => {
                const path = find(key, fileName)?.path;
                picked.current = picked.current.filter((p) => !(p.key === key && p.file.name === fileName));
                if (path) {
                    await deleteSupportingDocument(latest.current, path).catch(() => undefined);
                }
            },
            suggest: async (fileId) => {
                const entry = picked.current.find((p) => p.fileId === fileId);
                if (!entry) throw new Error("file is not in this preview");
                if (!entry.path) return {};
                return unwrap(
                    await formsFetch.POST("/api/preview/upload-suggestions", {
                        headers: authHeader(latest.current),
                        body: { questionKey: entry.key, path: entry.path },
                    }),
                    "get preview upload suggestions"
                );
            },
            suggestAnswers: async (_key, files) =>
                suggestAnswers(
                    latest.current,
                    picked.current.flatMap((p) => (p.path && files.some((f) => f.fileId === p.fileId) ? [p.path] : []))
                ),
        };
    }, []);
};
